import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import {
  exportTravelChartOfAccounts,
  todayFilenameStamp,
  type ExportableTravelAccountNode,
} from '@/lib/export-coa-travel';

type Scope = 'visible' | 'full';

interface TravelExportCoaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visibleTree: ExportableTravelAccountNode[];
  visibleCount: number;
}

export function TravelExportCoaDialog({
  open,
  onOpenChange,
  visibleTree,
  visibleCount,
}: TravelExportCoaDialogProps) {
  const { t } = useTranslation('accounting');
  const { t: tCommon } = useTranslation('common');
  const [scope, setScope] = useState<Scope>('visible');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [indented, setIndented] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const utils = trpc.useUtils();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      let treeToExport: ExportableTravelAccountNode[];
      if (scope === 'visible') {
        treeToExport = visibleTree;
      } else {
        treeToExport = (await utils.account.getTree.fetch({
          isActive: includeInactive ? undefined : true,
          includeMetadata: true,
        })) as ExportableTravelAccountNode[];
      }

      if (treeToExport.length === 0) {
        showErrorToast(t('export.empty'));
        return;
      }

      exportTravelChartOfAccounts(treeToExport, {
        indented,
        filename: `travel-chart-of-accounts-${todayFilenameStamp()}.xlsx`,
      });
      showSuccessToast(t('export.success'));
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unexpected error';
      showErrorToast(`${t('export.error')}: ${message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const isVisibleEmpty = scope === 'visible' && visibleCount === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('export.title')}</DialogTitle>
          <DialogDescription>{t('export.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Scope */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('export.scope')}</Label>
            <RadioGroup value={scope} onValueChange={(v) => setScope(v as Scope)}>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="visible" id="travel-export-scope-visible" />
                <Label
                  htmlFor="travel-export-scope-visible"
                  className="cursor-pointer text-sm font-normal leading-tight"
                >
                  {t('export.scopeVisible')}
                  <span className="text-muted-foreground block text-xs">
                    {t('export.scopeVisibleHint')} ({visibleCount})
                  </span>
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="full" id="travel-export-scope-full" />
                <Label
                  htmlFor="travel-export-scope-full"
                  className="cursor-pointer text-sm font-normal leading-tight"
                >
                  {t('export.scopeFull')}
                  <span className="text-muted-foreground block text-xs">
                    {t('export.scopeFullHint')}
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Include inactive (only meaningful for full scope) */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="travel-export-include-inactive"
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(checked === true)}
              disabled={scope !== 'full'}
            />
            <Label
              htmlFor="travel-export-include-inactive"
              className={`cursor-pointer text-sm font-normal ${scope !== 'full' ? 'text-muted-foreground' : ''}`}
            >
              {t('export.includeInactive')}
            </Label>
          </div>

          {/* Indent names */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="travel-export-indented"
              checked={indented}
              onCheckedChange={(checked) => setIndented(checked === true)}
            />
            <Label htmlFor="travel-export-indented" className="cursor-pointer text-sm font-normal">
              {t('export.indented')}
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
            className="h-8"
          >
            {tCommon('cancel')}
          </Button>
          <Button onClick={handleExport} disabled={isExporting || isVisibleEmpty} className="h-8">
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('export.exporting')}
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                {t('export.action')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
